import { useEffect, useState, useMemo } from 'react'
import {
  Plus,
  BookOpen,
  Loader2,
  Star,
  Users,
  Search,
  MoreHorizontal,
  SquarePen,
  Trash,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import { Checkbox } from '@/components/ui/checkbox'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { CategoryCombobox } from '@/components/ui/category-combobox'
import { usePromptStore } from '@/stores/prompt-store'
import { useAuthStore } from '@/stores/auth-store'
import { toast } from 'sonner'
import { cn } from '@/lib/utils'
import type { PromptTemplate } from '@/lib/api'

// ===================
// Types
// ===================

type FilterTab = 'all' | 'favorites' | 'my' | 'shared'

// ===================
// Page Component
// ===================

export function PromptsPage() {
  const {
    prompts,
    categories,
    isLoading,
    error,
    fetchPrompts,
    createPrompt,
    updatePrompt,
    deletePrompt,
    createCategory,
  } = usePromptStore()

  const user = useAuthStore((s) => s.user)
  const role = useAuthStore((s) => s.role)
  const currentUserId = user?.id ?? ''
  const isAdmin = role === 'admin'

  const [filter, setFilter] = useState<FilterTab>('all')
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null)
  const [searchQuery, setSearchQuery] = useState('')

  // Dialog state
  const [formOpen, setFormOpen] = useState(false)
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create')
  const [editingPrompt, setEditingPrompt] = useState<PromptTemplate | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<PromptTemplate | null>(null)

  // Form state
  const [formTitle, setFormTitle] = useState('')
  const [formContent, setFormContent] = useState('')
  const [formCategoryId, setFormCategoryId] = useState<string | null>(null)
  const [formVisibility, setFormVisibility] = useState<'personal' | 'shared'>('personal')
  const [formIsPinned, setFormIsPinned] = useState(false)
  const [isSaving, setIsSaving] = useState(false)

  useEffect(() => {
    void fetchPrompts()
  }, [fetchPrompts])

  // Filter prompts client-side
  const filteredPrompts = useMemo(() => {
    let result = prompts

    // Tab filter
    switch (filter) {
      case 'favorites':
        result = result.filter((p) => p.isPinned)
        break
      case 'my':
        result = result.filter((p) => p.createdBy === currentUserId)
        break
      case 'shared':
        result = result.filter((p) => p.visibility === 'shared')
        break
    }

    // Category filter
    if (selectedCategoryId === 'uncategorized') {
      result = result.filter((p) => !p.categoryId)
    } else if (selectedCategoryId) {
      result = result.filter((p) => p.categoryId === selectedCategoryId)
    }

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      result = result.filter(
        (p) =>
          p.title.toLowerCase().includes(query) ||
          p.content.toLowerCase().includes(query)
      )
    }

    return result
  }, [prompts, filter, selectedCategoryId, searchQuery, currentUserId])

  // Actions
  const handleCreate = () => {
    setEditingPrompt(null)
    setFormTitle('')
    setFormContent('')
    setFormCategoryId(null)
    setFormVisibility('personal')
    setFormIsPinned(false)
    setFormMode('create')
    setFormOpen(true)
  }

  const handleEdit = (prompt: PromptTemplate) => {
    setEditingPrompt(prompt)
    setFormTitle(prompt.title)
    setFormContent(prompt.content)
    setFormCategoryId(prompt.categoryId)
    setFormVisibility(prompt.visibility)
    setFormIsPinned(prompt.isPinned)
    setFormMode('edit')
    setFormOpen(true)
  }

  const handleFormSave = async () => {
    if (!formTitle.trim() || !formContent.trim() || isSaving) return

    setIsSaving(true)
    try {
      if (formMode === 'edit' && editingPrompt) {
        await updatePrompt(editingPrompt.id, {
          title: formTitle.trim(),
          content: formContent.trim(),
          categoryId: formCategoryId,
          visibility: formVisibility,
          isPinned: formIsPinned,
        })
        toast.success('Prompt updated')
      } else {
        await createPrompt({
          title: formTitle.trim(),
          content: formContent.trim(),
          categoryId: formCategoryId,
          visibility: formVisibility,
          isPinned: formIsPinned,
        })
        toast.success('Prompt created')
      }
      setFormOpen(false)
    } catch {
      toast.error(formMode === 'edit' ? 'Failed to update prompt' : 'Failed to create prompt')
    } finally {
      setIsSaving(false)
    }
  }

  const handleTogglePin = async (prompt: PromptTemplate) => {
    try {
      await updatePrompt(prompt.id, { isPinned: !prompt.isPinned })
    } catch {
      toast.error('Failed to update favorite')
    }
  }

  const handleToggleVisibility = async (prompt: PromptTemplate) => {
    const newVisibility = prompt.visibility === 'personal' ? 'shared' : 'personal'
    try {
      await updatePrompt(prompt.id, { visibility: newVisibility })
      toast.success(newVisibility === 'shared' ? 'Prompt shared with organisation' : 'Prompt set to personal')
    } catch {
      toast.error('Failed to update visibility')
    }
  }

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    try {
      await deletePrompt(deleteTarget.id)
      toast.success('Prompt deleted')
    } catch {
      toast.error('Failed to delete prompt')
    } finally {
      setDeleteTarget(null)
    }
  }

  const canModify = (prompt: PromptTemplate) =>
    prompt.createdBy === currentUserId || (isAdmin && prompt.visibility === 'shared')

  // Loading state
  if (isLoading && prompts.length === 0) {
    return (
      <div className="flex flex-col h-full items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground mt-2">Loading prompts...</p>
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Toolbar */}
      <div className="bg-background px-6 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            {/* Filter tabs */}
            <Tabs value={filter} onValueChange={(v) => setFilter(v as FilterTab)}>
              <TabsList className="bg-muted p-1 h-9 rounded-lg">
                <TabsTrigger value="all" className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm">All</TabsTrigger>
                <TabsTrigger value="favorites" className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm">Favorites</TabsTrigger>
                <TabsTrigger value="my" className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm">My Prompts</TabsTrigger>
                <TabsTrigger value="shared" className="h-7 px-3 rounded-md data-[state=active]:bg-card data-[state=active]:shadow-sm">Shared</TabsTrigger>
              </TabsList>
            </Tabs>

            {/* Search */}
            <div className="relative max-w-xs flex-1">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search prompts..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-8"
              />
            </div>
          </div>

          {/* Actions */}
          <Button size="sm" className="gap-1.5" onClick={handleCreate}>
            <Plus className="h-4 w-4" />
            New Prompt
          </Button>
        </div>

        {/* Category chips */}
        {categories.length > 0 && (
          <div className="flex gap-1.5 flex-wrap mt-3">
            <button
              className={cn(
                "px-2.5 py-1 rounded-lg text-xs transition-colors border",
                !selectedCategoryId
                  ? "bg-foreground text-background border-foreground"
                  : "bg-transparent text-muted-foreground border-border hover:bg-accent"
              )}
              onClick={() => setSelectedCategoryId(null)}
            >
              All
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                className={cn(
                  "px-2.5 py-1 rounded-lg text-xs transition-colors border",
                  selectedCategoryId === category.id
                    ? "bg-foreground text-background border-foreground"
                    : "bg-transparent text-muted-foreground border-border hover:bg-accent"
                )}
                onClick={() => setSelectedCategoryId(category.id)}
              >
                {category.name}
              </button>
            ))}
            <button
              className={cn(
                "px-2.5 py-1 rounded-lg text-xs transition-colors border",
                selectedCategoryId === 'uncategorized'
                  ? "bg-foreground text-background border-foreground"
                  : "bg-transparent text-muted-foreground border-border hover:bg-accent"
              )}
              onClick={() => setSelectedCategoryId('uncategorized')}
            >
              Uncategorized
            </button>
          </div>
        )}
      </div>

      {/* Error Banner */}
      {error && (
        <div className="bg-destructive/10 border-b border-destructive/20 px-6 py-2 flex items-center justify-between">
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="ghost" size="sm" onClick={() => void fetchPrompts(true)}>Try again</Button>
        </div>
      )}

      {/* Content */}
      <div className="flex-1 overflow-y-auto px-6 py-4">
        {filteredPrompts.length === 0 ? (
          <EmptyState filter={filter} searchQuery={searchQuery} onCreateClick={handleCreate} />
        ) : (
          <div className="overflow-x-auto rounded-lg border bg-card shadow-sm">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 border-b">
                  <th className="px-4 py-3 w-10"></th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground">Title</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-32">Category</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-24">Visibility</th>
                  <th className="px-4 py-3 text-left text-sm font-medium text-muted-foreground w-28">Creator</th>
                  <th className="px-4 py-3 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {filteredPrompts.map((prompt) => (
                  <tr key={prompt.id} className="border-b last:border-b-0 hover:bg-muted/30 transition-colors">
                    {/* Favorite star */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => void handleTogglePin(prompt)}
                        className="text-muted-foreground hover:text-amber-500 transition-colors"
                        title={prompt.isPinned ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Star className={cn("h-4 w-4", prompt.isPinned && "text-amber-500 fill-amber-500")} />
                      </button>
                    </td>

                    {/* Title + preview */}
                    <td className="px-4 py-3">
                      <div className="font-medium text-sm">{prompt.title}</div>
                      <div className="text-meta line-clamp-1 mt-0.5 max-w-md">
                        {prompt.content}
                      </div>
                    </td>

                    {/* Category */}
                    <td className="px-4 py-3">
                      {prompt.categoryName ? (
                        <Badge variant="outline" className="text-xs">
                          {prompt.categoryName}
                        </Badge>
                      ) : (
                        <span className="text-meta">&mdash;</span>
                      )}
                    </td>

                    {/* Visibility */}
                    <td className="px-4 py-3">
                      {prompt.visibility === 'shared' ? (
                        <Badge variant="outline" className="text-xs">
                          <Users className="h-3 w-3 mr-1" />
                          Shared
                        </Badge>
                      ) : (
                        <span className="text-meta">Personal</span>
                      )}
                    </td>

                    {/* Creator */}
                    <td className="px-4 py-3 text-sm text-muted-foreground truncate">
                      {prompt.createdBy === currentUserId ? 'You' : prompt.creatorName}
                    </td>

                    {/* Actions */}
                    <td className="px-4 py-3">
                      {canModify(prompt) && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" className="h-7 w-7">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleEdit(prompt)}>
                              <SquarePen className="h-4 w-4 mr-2" />
                              Edit
                            </DropdownMenuItem>
                            {prompt.createdBy === currentUserId && (
                              <DropdownMenuItem onClick={() => void handleToggleVisibility(prompt)}>
                                <Users className="h-4 w-4 mr-2" />
                                {prompt.visibility === 'shared' ? 'Make Personal' : 'Share with Team'}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem
                              className="text-destructive focus:text-destructive"
                              onClick={() => setDeleteTarget(prompt)}
                            >
                              <Trash className="h-4 w-4 mr-2" />
                              Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{formMode === 'edit' ? 'Edit Prompt' : 'Create New Prompt'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="page-form-title">Title</Label>
              <Input
                id="page-form-title"
                placeholder="e.g., Technical Requirements Extract"
                value={formTitle}
                onChange={(e) => setFormTitle(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="page-form-content">Prompt</Label>
              <Textarea
                id="page-form-content"
                placeholder="Enter your prompt..."
                value={formContent}
                onChange={(e) => setFormContent(e.target.value)}
                className="min-h-[120px]"
              />
            </div>

            <div className="flex gap-3">
              <div className="flex-1 space-y-2">
                <Label>Category</Label>
                <CategoryCombobox
                  categories={categories}
                  value={formCategoryId}
                  onChange={setFormCategoryId}
                  onCreateCategory={createCategory}
                />
              </div>
              <div className="space-y-2">
                <Label>Visibility</Label>
                <div className="flex rounded-lg border border-border overflow-hidden h-9">
                  <button
                    type="button"
                    className={cn(
                      "px-3 text-xs transition-colors",
                      formVisibility === 'personal'
                        ? "bg-foreground text-background font-medium"
                        : "hover:bg-accent text-muted-foreground"
                    )}
                    onClick={() => setFormVisibility('personal')}
                  >
                    Personal
                  </button>
                  <button
                    type="button"
                    className={cn(
                      "px-3 text-xs transition-colors flex items-center gap-1",
                      formVisibility === 'shared'
                        ? "bg-foreground text-background font-medium"
                        : "hover:bg-accent text-muted-foreground"
                    )}
                    onClick={() => setFormVisibility('shared')}
                  >
                    <Users className="h-3 w-3" />
                    Shared
                  </button>
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="page-form-pin"
                checked={formIsPinned}
                onCheckedChange={(checked) => setFormIsPinned(checked === true)}
              />
              <Label htmlFor="page-form-pin" className="flex items-center gap-1.5 cursor-pointer">
                <Star className="h-3.5 w-3.5" />
                Add to favorites
              </Label>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="ghost" onClick={() => setFormOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => void handleFormSave()}
                disabled={!formTitle.trim() || !formContent.trim() || isSaving}
              >
                {isSaving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
                {formMode === 'edit' ? 'Save Changes' : 'Save Prompt'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteTarget} onOpenChange={(open) => !open && setDeleteTarget(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete &ldquo;{deleteTarget?.title}&rdquo;?</AlertDialogTitle>
            <AlertDialogDescription>
              This can&apos;t be undone. This prompt will be permanently deleted.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDeleteConfirm()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

// ===================
// Empty State
// ===================

function EmptyState({
  filter,
  searchQuery,
  onCreateClick,
}: {
  filter: FilterTab
  searchQuery: string
  onCreateClick: () => void
}) {
  if (searchQuery.trim()) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Search className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        <h3 className="text-base font-medium mb-1">No results</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          No prompts match &ldquo;{searchQuery}&rdquo;. Try a different search term.
        </p>
      </div>
    )
  }

  if (filter === 'favorites') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Star className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        <h3 className="text-base font-medium mb-1">No favorites yet</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          Star prompts you use often to find them quickly here.
        </p>
      </div>
    )
  }

  if (filter === 'my') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <BookOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        <h3 className="text-base font-medium mb-1">No prompts yet</h3>
        <p className="text-sm text-muted-foreground mb-4 max-w-sm">
          You haven&apos;t created any prompts yet. Save prompts you use often for quick access.
        </p>
        <Button onClick={onCreateClick}>
          <Plus className="h-4 w-4 mr-2" />
          New Prompt
        </Button>
      </div>
    )
  }

  if (filter === 'shared') {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <Users className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
        <h3 className="text-base font-medium mb-1">No shared prompts</h3>
        <p className="text-sm text-muted-foreground max-w-sm">
          No shared prompts in your organisation yet. Create a prompt and share it with your team.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <BookOpen className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
      <h3 className="text-base font-medium mb-1">Prompt Library</h3>
      <p className="text-sm text-muted-foreground mb-4 max-w-sm">
        Save and organise prompts you use often. Share them with your team or keep them personal.
      </p>
      <Button onClick={onCreateClick}>
        <Plus className="h-4 w-4 mr-2" />
        New Prompt
      </Button>
    </div>
  )
}
